import React, { useState } from "react";
import { LensMark } from "./pdUtil";

// TemplateFreeRead — any extraction Lens read without a template (e.g. a home BP log).
// Replaces the old generic key/value loop. Marks every key it renders in `used`
// so "ALSO ON THIS DOCUMENT" never repeats them.

const NOTE_RX = /(^|_)(notes?|remarks|comments?)$/;
const isEmpty = (v) => v == null || v === "" || v === "\u2014" || (Array.isArray(v) && v.length === 0);
const human = (k) => { const s = String(k).replace(/_raw$/, "").replace(/_/g, " ").replace(/\bbp\b/i, "BP"); return s.charAt(0).toUpperCase() + s.slice(1); };
const shortHead = (k) => human(k).replace(/^Measurement /, "").replace(/ BP$/, "");
const titleCase = (s) => String(s).toLowerCase().replace(/\b[a-z]/g, (c) => c.toUpperCase());
const sentences = (t) => String(t).split(/(?<=\.)\s+(?=[A-Z0-9])/).map((x) => x.trim()).filter(Boolean);
const fmt = (v) =>
  v === true ? "Yes" : v === false ? "No"
    : typeof v === "string" && /^[a-z]+(_[a-z]+)+$/.test(v) ? human(v)
      : Array.isArray(v) ? v.map(fmt).join("; ")
        : v && typeof v === "object" ? Object.entries(v).filter(([, x]) => !isEmpty(x)).map(([k, x]) => `${human(k)} ${fmt(x)}`).join(" \u00b7 ")
          : String(v);
const bpCheck = (s, d) => (s == null || d == null) ? "Unclear" : (s < 70 || s > 250 || d < 35 || d > 140 || s <= d) ? "Check" : "";

export default function TemplateFreeRead({ typeLabel, extraction, used }) {
  const keys = Object.keys(extraction).filter((k) => k !== "extraction_confidence" && !used.has(k));
  const tables = [], notes = [], facts = [], prose = [];
  keys.forEach((k) => {
    const v = extraction[k]; used.add(k); if (isEmpty(v)) return;
    if (Array.isArray(v) && v[0] && typeof v[0] === "object") tables.push(k);
    else if (typeof v === "string" && NOTE_RX.test(k)) notes.push(k);
    else if (typeof v === "string" && v.length > 80) prose.push(k);
    else if (k !== "document_title") facts.push([human(k), fmt(v)]);
  });
  const first = tables.length ? extraction[tables[0]] : null;
  const dk = first && Object.keys(first[0]).find((x) => /date_raw$/.test(x));
  const range = dk ? `${titleCase(first[0][dk])} \u2013 ${titleCase(first[first.length - 1][dk])}` : "";
  return (
    <div className="tfr" data-testid="tfr">
      <div>
        <div className="tfr-k">{typeLabel} · read without a template</div>
        <h3 className="tfr-title">{titleCase(extraction.document_title || typeLabel)}</h3>
        {first ? <div className="tfr-meta">{first.length} {human(tables[0]).toLowerCase()}{range ? ` \u00b7 ${range}` : ""}</div> : null}
      </div>
      {notes.map((k) => <LensNotes key={k} label={human(k)} text={extraction[k]} />)}
      {tables.map((k) => <RecordTable key={k} name={k} rows={extraction[k]} />)}
      {facts.length > 0 ? <div className="tfr-facts">{facts.map(([l, v]) => <div key={l}><div className="tfr-fk">{l}</div><div className="tfr-fv">{v}</div></div>)}</div> : null}
      {prose.map((k) => <section key={k}><div className="tfr-k">{human(k)}</div><p className="tfr-prose">{extraction[k]}</p></section>)}
    </div>
  );
}

function LensNotes({ label, text }) {
  const [all, setAll] = useState(false);
  const ss = sentences(text), show = all ? ss : ss.slice(0, 3);
  return (
    <section className="tfr-notes" data-testid="tfr-lens-notes">
      <div className="tfr-notes-h"><LensMark size={13} color="#5856D6" /> <b>How Lens read this</b><span>{label}</span></div>
      <ul>{show.map((s, i) => <li key={i}>{s}</li>)}</ul>
      {ss.length > 3 ? <button className="tfr-link" data-testid="tfr-notes-toggle" onClick={() => setAll(!all)}>{all ? "Show less" : `Show all ${ss.length} notes`}</button> : null}
    </section>
  );
}

function RecordTable({ name, rows }) {
  const [asWritten, setAsWritten] = useState(false);
  const ks = [...new Set(rows.flatMap((r) => Object.keys(r)))];
  const noteK = ks.filter((k) => NOTE_RX.test(k));
  const sysK = ks.find((k) => /systolic/.test(k)), diaK = ks.find((k) => /diastolic/.test(k));
  const cols = [];
  ks.forEach((k) => {
    if (noteK.includes(k) || k === diaK) return;
    if (/_raw$/.test(k) && ks.includes(k.slice(0, -4))) return;              // the _raw twin shares its field's column
    if (k === sysK && diaK) { cols.push({ k: "bp", label: "Blood pressure", num: true }); return; }
    const raw = ks.includes(k + "_raw") ? k + "_raw" : null;
    if (rows.every((r) => isEmpty(r[k]) && (!raw || isEmpty(r[raw])))) return; // drop all-empty columns
    const allNull = !!raw && rows.every((r) => isEmpty(r[k]));
    const num = rows.some((r) => typeof r[k] === "number") && rows.every((r) => isEmpty(r[k]) || typeof r[k] === "number");
    cols.push({ k, raw, allNull, num, label: shortHead(k) });
  });
  let flagged = 0;
  const body = rows.map((r, i) => {
    let bad = false;
    const cells = cols.map((c) => {
      if (c.k === "bp") {
        const s = r[sysK], d = r[diaK], ck = bpCheck(s, d); if (ck) bad = true;
        return <td key="bp" className={"n" + (ck ? " asw" : "")}>
          {s == null && d == null ? <span className="na">—</span> : <><b>{s ?? "?"}</b><span className="sl">/</span><b>{d ?? "?"}</b></>}
          {ck ? <span className="tag">{ck}</span> : null}</td>;
      }
      const v = r[c.k], raw = c.raw ? r[c.raw] : null;
      if (asWritten && !isEmpty(raw)) return <td key={c.k} className="mono wr">{raw}</td>;
      if (isEmpty(v) && !isEmpty(raw)) {
        if (c.allNull) return <td key={c.k}>{titleCase(raw)}</td>;
        bad = true;
        return <td key={c.k} className="asw" title="Lens couldn't read this cleanly. Shown as written.">{raw}<span className="tag">As written</span></td>;
      }
      if (isEmpty(v)) return <td key={c.k}><span className="na">—</span></td>;
      return <td key={c.k} className={c.num ? "n" : ""} title={raw ? `Written: ${raw}` : undefined}>{fmt(v)}</td>;
    });
    if (bad) flagged++;
    const note = noteK.map((k) => r[k]).filter((x) => !isEmpty(x)).join(" ");
    return [
      <tr key={i} className={(bad ? "bad " : "") + (note ? "hasn" : "")}>{cells}</tr>,
      note ? <tr key={i + "n"} className={"nrow" + (bad ? " bad" : "")}><td colSpan={cols.length}>{note}</td></tr> : null,
    ];
  });
  const dateCol = cols.find((c) => c.allNull);
  return (
    <section className="tfr-tw" data-testid={`tfr-table-${name}`}>
      <div className="tfr-th">
        <span className="tfr-k">{human(name)} · {rows.length}</span>
        {flagged ? <span className="tfr-am">{flagged} to check against the original</span> : <span className="tfr-faint">All read cleanly</span>}
        <span className="sp" />
        <div className="seg sm" role="tablist">
          <button type="button" role="tab" aria-selected={!asWritten} className={asWritten ? "" : "on"} data-testid="tfr-lens-read" onClick={() => setAsWritten(false)}>Lens read</button>
          <button type="button" role="tab" aria-selected={asWritten} className={asWritten ? "on" : ""} data-testid="tfr-as-written" onClick={() => setAsWritten(true)}>As written</button>
        </div>
      </div>
      {sysK && diaK ? <BpGlance rows={rows} sK={sysK} dK={diaK} dateK={ks.find((k) => /date_raw$/.test(k))} /> : null}
      <div className="tfr-scroll">
        <table className="tfr-t">
          <thead><tr>{cols.map((c) => <th key={c.k} className={c.num ? "n" : ""}>{c.label}
            {c.allNull && !asWritten ? <span className="thn">as written</span> : null}{c.k === "bp" ? <span className="thn">mmHg</span> : null}</th>)}</tr></thead>
          <tbody>{body}</tbody>
        </table>
      </div>
      {dateCol && !asWritten ? <div className="tfr-foot">No year on the document, so Lens kept the dates as written.</div> : null}
    </section>
  );
}

function BpGlance({ rows, sK, dK, dateK }) {
  const W = 640, H = 150, L = 34, R = 8, T = 10, B = 24, lo = 50, hi = 170, step = (W - L - R) / Math.max(1, rows.length);
  const y = (v) => T + (hi - Math.max(lo, Math.min(hi, v))) / (hi - lo) * (H - T - B);
  const lab = (s) => titleCase(String(s || "")).replace(/\s*-\s*/, " ").replace(/^(\w{3})\w*/, "$1");
  return (
    <div className="tfr-bp" data-testid="tfr-bp-glance">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Blood pressure readings, diastolic to systolic">
        {[80, 120, 140].map((v) => <g key={v}><line x1={L} x2={W - R} y1={y(v)} y2={y(v)} className={"gl" + (v !== 140 ? " ref" : "")} /><text x={L - 6} y={y(v) + 3.5} textAnchor="end" className="ax">{v}</text></g>)}
        {rows.map((r, i) => {
          const x = L + step * i + step / 2, s = r[sK], d = r[dK], ok = !bpCheck(s, d);
          return <g key={i}>
            {ok ? <><line x1={x} x2={x} y1={y(s)} y2={y(d)} className="bar" /><circle cx={x} cy={y(s)} r="3.2" className="sy" /><circle cx={x} cy={y(d)} r="3.2" className="dy" /></>
              : <text x={x} y={H - B - 4} textAnchor="middle" className="unc">?</text>}
            {dateK && (i === 0 || i === rows.length - 1 || i % 3 === 0) ? <text x={x} y={H - 6} textAnchor="middle" className="ax">{lab(r[dateK])}</text> : null}
          </g>;
        })}
      </svg>
      <div className="tfr-legend"><span><i className="sy" />Systolic</span><span><i className="dy" />Diastolic</span><span><i className="ref" />120 / 80</span><span><b>?</b>Unclear, see the table</span></div>
    </div>
  );
}
